import { problemError } from "./api";

/**
 * A minimal typing of the slice of Google Identity Services we use.
 *
 * Hand-written rather than pulled from @types/google.accounts: we touch three
 * methods, and a local declaration that matches our usage is easier to audit
 * than a dependency covering the whole surface.
 */
export interface GoogleCredentialResponse {
  /** A signed JWT ID token. Opaque here — only the API verifies it. */
  credential: string;
}

interface GoogleIdentityServices {
  accounts: {
    id: {
      initialize(config: {
        client_id: string;
        callback: (response: GoogleCredentialResponse) => void;
        auto_select?: boolean;
        cancel_on_tap_outside?: boolean;
      }): void;
      renderButton(
        parent: HTMLElement,
        options: {
          type?: "standard" | "icon";
          theme?: "outline" | "filled_blue" | "filled_black";
          size?: "small" | "medium" | "large";
          text?: "signin_with" | "signup_with" | "continue_with";
          shape?: "rectangular" | "pill";
          width?: number;
        },
      ): void;
      disableAutoSelect(): void;
    };
  };
}

declare global {
  interface Window {
    google?: GoogleIdentityServices;
  }
}

const GIS_SRC = "https://accounts.google.com/gsi/client";

let loader: Promise<GoogleIdentityServices> | null = null;

/** Load the GIS script once, resolving with the global it installs. */
export function loadGoogleIdentity(): Promise<GoogleIdentityServices> {
  if (loader) return loader;

  loader = new Promise((resolve, reject) => {
    if (window.google) {
      resolve(window.google);
      return;
    }

    const script = document.createElement("script");
    script.src = GIS_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.google) resolve(window.google);
      else reject(new Error("Google Identity Services loaded but installed no global"));
    };
    script.onerror = () => {
      // Let a later attempt retry rather than caching the failure forever.
      loader = null;
      reject(new Error("Could not load Google Identity Services"));
    };
    document.head.appendChild(script);
  });

  return loader;
}

/**
 * Exchange a Google ID token for a scenix session.
 *
 * The token is verified server-side — signature, issuer, audience, expiry. The
 * browser deliberately does not decode it: nothing here is trusted, and the
 * only thing that comes back is an HttpOnly session cookie.
 */
export async function exchangeCredential(credential: string): Promise<void> {
  const response = await fetch("/v1/auth/google", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ credential }),
  });

  if (!response.ok) {
    // The API's own words, not the status. A refusal at the door is the last
    // thing that happens to somebody who cannot get in, and telling them
    // "Sign-in failed (403)" threw away the one sentence that says whether
    // their invitation ran out and what to do about it (ADR 000109).
    throw await problemError(response);
  }
}
